/**
 * Cart upsells — add/remove the two add-on products (Persönliche Widmung,
 * Matt & Kratzfestes Cover) directly from the cart drawer / cart page.
 *
 * Markup lives in snippets/cart-products.liquid. The relevant hooks are:
 *   [data-upsell-toggle]  → reveals the inline Widmung textarea
 *   [data-upsell-add]     → POST /cart/add.js   (data-kind, data-variant-id, data-book-key)
 *   [data-upsell-remove]  → POST /cart/change.js with quantity 0 (data-line-key)
 *   [data-remove-book]    → POST /cart/update.js removing the book + its add-ons
 *
 * After any change we dispatch the theme's `cart:update` event so
 * `cart-items-component` (assets/component-cart-items.js) re-renders its section
 * via the existing morph path — no manual DOM rebuilding needed here.
 */

const CART_UPDATE_EVENT = 'cart:update';

/** Dispatch the theme cart-update event so the cart section re-renders. */
function notifyCartUpdated() {
  document.dispatchEvent(
    new CustomEvent(CART_UPDATE_EVENT, {
      bubbles: true,
      // Matches the shape component-cart-items.js reads; empty `sections`
      // makes it fall back to re-fetching + morphing its own section.
      detail: { resource: null, sourceId: 'cart-upsell', data: {} },
    })
  );
}

/** @param {HTMLElement | null} el */
function setLoading(el, loading) {
  const card = el?.closest('.cart-upsell');
  if (card) card.classList.toggle('is-loading', loading);
}

/**
 * @param {string} url
 * @param {object} body
 * @returns {Promise<boolean>} success
 */
async function postCart(url, body) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      console.error('[cart-upsells] request failed', url, await response.text());
      return false;
    }
    return true;
  } catch (error) {
    console.error('[cart-upsells] request error', error);
    return false;
  }
}

async function addUpsell(/** @type {HTMLElement} */ button) {
  const variantId = parseInt(button.dataset.variantId || '', 10);
  const bookKey = button.dataset.bookKey || '';
  const kind = button.dataset.kind || '';
  if (!variantId) return;

  /** @type {Record<string, string>} */
  const properties = {};
  if (bookKey) properties._BuchID = bookKey;

  if (kind === 'dedication') {
    const card = button.closest('.cart-upsell');
    const textarea = /** @type {HTMLTextAreaElement | null} */ (card?.querySelector('.cart-upsell__textarea'));
    const text = (textarea?.value || '').trim();
    if (!text) {
      textarea?.focus();
      return;
    }
    properties['Widmungs Text'] = text;
  }

  setLoading(button, true);
  const ok = await postCart('/cart/add.js', {
    items: [{ id: variantId, quantity: 1, properties }],
  });
  if (ok) {
    notifyCartUpdated();
  } else {
    setLoading(button, false);
    alert('Konnte nicht zum Warenkorb hinzugefügt werden. Bitte erneut versuchen.');
  }
}

async function removeUpsell(/** @type {HTMLElement} */ button) {
  const lineKey = button.dataset.lineKey || '';
  if (!lineKey) return;

  setLoading(button, true);
  const ok = await postCart('/cart/change.js', { id: lineKey, quantity: 0 });
  if (ok) {
    notifyCartUpdated();
  } else {
    setLoading(button, false);
    alert('Konnte nicht entfernt werden. Bitte erneut versuchen.');
  }
}

async function removeBook(/** @type {HTMLElement} */ button) {
  const keys = (button.dataset.removeKeys || '').split(',').map((k) => k.trim()).filter(Boolean);
  if (!keys.length) return;

  /** @type {Record<string, number>} */
  const updates = {};
  keys.forEach((key) => {
    updates[key] = 0;
  });

  button.disabled = true;
  const ok = await postCart('/cart/update.js', { updates });
  if (ok) {
    notifyCartUpdated();
  } else {
    button.disabled = false;
    alert('Konnte nicht entfernt werden. Bitte erneut versuchen.');
  }
}

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);

  const toggle = target.closest('[data-upsell-toggle]');
  if (toggle) {
    const form = toggle.closest('.cart-upsell')?.querySelector('.cart-upsell__form');
    if (form) {
      form.hidden = !form.hidden;
      // Reflect the open/closed state on the switch so it animates on/off.
      toggle.setAttribute('aria-checked', form.hidden ? 'false' : 'true');
      if (!form.hidden) {
        /** @type {HTMLTextAreaElement | null} */ (form.querySelector('.cart-upsell__textarea'))?.focus();
      }
    }
    return;
  }

  const addBtn = target.closest('[data-upsell-add]');
  if (addBtn) {
    addUpsell(/** @type {HTMLElement} */ (addBtn));
    return;
  }

  const removeBtn = target.closest('[data-upsell-remove]');
  if (removeBtn) {
    removeUpsell(/** @type {HTMLElement} */ (removeBtn));
    return;
  }

  const removeBookBtn = target.closest('[data-remove-book]');
  if (removeBookBtn) {
    removeBook(/** @type {HTMLElement} */ (removeBookBtn));
  }
});
